import React, { useEffect, useState, useRef, useContext } from 'react';
import { dbPost } from '../../apis/commonApi';
import { ModalContext } from '../../components/contexts';
import TextEditor from '../../components/inputComponents/TextEditor';
import GbitTablePaging from '../../components/tableComponents/GbitTablePaging';

const AdminTOS = () => {
  useEffect(() => {
    document.title = '개인정보취급';
  }, []);
  const { modalService } = useContext(ModalContext);
  const [terms, setTerms] = useState([]);
  const [isWriting, setIsWriting] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [useYn, setUseYn] = useState('Y');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const editorRef = useRef(null);

  const fetchTerms = async () => {
    try {
      const url = '/member/mngTrms/search.do';
      const filters = { type: 'P' };
      const response = await dbPost(url, filters);
      const sortedTerms = Array.isArray(response)
        ? response.sort((a, b) => new Date(b.crtYmd) - new Date(a.crtYmd))
        : [];
      setTerms(sortedTerms);
    } catch (error) {
      setTerms([]);
    }
  };

  useEffect(() => {
    fetchTerms();
  }, []);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const paginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return terms.slice(startIndex, startIndex + itemsPerPage);
  };

  const resetFormState = () => {
    setTitle('');
    setContent('');
    setUseYn('Y');
    setStartDate('');
    setEndDate('');
    setSelectedTerm(null);
  };

  const handleRegister = () => handleSave(false);
  const handleUpdate = () => handleSave(true);

  const handleSave = async (isUpdate = false) => {
    const editorInstance = editorRef.current?.getInstance();
    const markdownContent = editorInstance?.getMarkdown() || '';

    const missingFields = [];
    if (!title.trim()) missingFields.push('제목');
    if (!markdownContent.trim()) missingFields.push('내용');
    if (!startDate.trim()) missingFields.push('시작 날짜');
    if (!endDate.trim()) missingFields.push('종료 날짜');
    if (!useYn.trim()) missingFields.push('사용 여부');

    if (missingFields.length > 0) {
      modalService.errorAlert(
        `${missingFields.join(', ')}을(를) 입력해주세요.`
      );
      return;
    }

    const formatToYYYYMMDD = date => date.replace(/-/g, '');

    const payload = {
      trmsTtl: title,
      trmsCn: markdownContent,
      strtYmd: formatToYYYYMMDD(startDate),
      endYmd: formatToYYYYMMDD(endDate),
      useYn,
      ...(isUpdate && { id: selectedTerm?.trmsId }),
      type: 'P',
    };

    try {
      const url = isUpdate
        ? '/member/mngTrms/update.do'
        : '/member/mngTrms/create.do';
      await dbPost(url, payload);

      modalService.successAlert(
        isUpdate
          ? '항목이 성공적으로 수정되었습니다.'
          : '항목이 성공적으로 등록되었습니다.'
      );
      resetFormState();
      setIsWriting(false);
      fetchTerms();
    } catch (error) {
      modalService.errorAlert(
        isUpdate
          ? '수정 중 문제가 발생했습니다.'
          : '등록 중 문제가 발생했습니다.'
      );
    }
  };

  const formatDate = dateString => {
    if (!dateString || dateString.length !== 8) return '';
    return `${dateString.slice(0, 4)}-${dateString.slice(
      4,
      6
    )}-${dateString.slice(6)}`;
  };

  const handleTermClick = term => {
    setSelectedTerm(term);
    setTitle(term.trmsTtl);
    setContent(term.trmsCn);
    setStartDate(formatDate(term.strtYmd));
    setEndDate(formatDate(term.endYmd));
    setUseYn(term.useYn);
    setIsWriting(true);
  };

  const handleCancelWriting = () => {
    resetFormState();
    setIsWriting(false);
  };

  const handleStartDateChange = e => {
    const newStartDate = e.target.value;
    if (endDate && newStartDate > endDate) {
      modalService.errorAlert('시작 날짜는 종료 날짜보다 늦을 수 없습니다.');
      return;
    }
    setStartDate(newStartDate);
  };

  const handleEndDateChange = e => {
    const newEndDate = e.target.value;
    if (!startDate) {
      modalService.errorAlert('먼저 시작 날짜를 선택하세요.');
      return;
    }
    if (newEndDate < startDate) {
      modalService.errorAlert('종료 날짜는 시작 날짜보다 이를 수 없습니다.');
      return;
    }
    setEndDate(newEndDate);
  };

  const renderList = () => (
    <div className="contents">
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">개인정보취급관리</h2>
      </div>
      <div className="tbl-wrap">
        <table className="tbl col data scroll">
          <colgroup>
            <col style={{ width: '10%' }} />
            <col style={{ width: '30%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>No</th>
              <th>제목</th>
              <th>사용 여부</th>
              <th>시작 날짜</th>
              <th>종료 날짜</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData().map((term, index) => (
              <tr
                key={term.trmsId}
                onClick={() => handleTermClick(term)}
                className="clickable-row"
              >
                <td>{index + 1}</td>
                <td>{term.trmsTtl}</td>
                <td>{term.useYn === 'Y' ? '사용' : '사용안함'}</td>
                <td>{formatDate(term.strtYmd)}</td>
                <td>{formatDate(term.endYmd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="page-btn-wrap both">
        <GbitTablePaging
          totalItems={terms.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
        <button
          type="button"
          className="btn xlg primary"
          onClick={() => setIsWriting(true)}
        >
          등록하기
        </button>
      </div>
    </div>
  );

  const renderForm = () => (
    <div className="contents">
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">
          개인정보취급 {selectedTerm ? '수정' : '등록'} 화면
        </h2>
      </div>
      <div className="tbl-wrap">
        <table className="tbl data row">
          <tbody>
            <tr>
              <th>제목</th>
              <td>
                <label className="hidden-label" htmlFor="title">
                  라벨
                </label>
                <input
                  type="text"
                  aria-labelledby="title"
                  className="form-control"
                  placeholder="제목을 입력해주세요."
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  id="title"
                />
              </td>
            </tr>
            <tr>
              <th>내용</th>
              <td>
                {' '}
                <label className="hidden-label" htmlFor="content">
                  라벨
                </label>
                <TextEditor
                  ref={editorRef}
                  initialValue={selectedTerm ? selectedTerm.trmsCn : ''}
                  id="content"
                />
              </td>
            </tr>
            <tr>
              <th>시작 날짜</th>
              <td>
                <label className="hidden-label" htmlFor="startDate">
                  라벨
                </label>
                <input
                  type="date"
                  aria-labelledby="startDate"
                  className="form-control"
                  value={startDate}
                  onChange={handleStartDateChange}
                  id="startDate"
                />
              </td>
            </tr>
            <tr>
              <th>종료 날짜</th>
              <td>
                <label className="hidden-label" htmlFor="entDate">
                  라벨
                </label>
                <input
                  type="date"
                  aria-labelledby="endDate"
                  className="form-control"
                  value={endDate}
                  onChange={handleEndDateChange}
                  id="endDate"
                />
              </td>
            </tr>
            <tr>
              <th>사용 여부</th>
              <td>
                <label className="hidden-label" htmlFor="useYn">
                  라벨
                </label>
                <select
                  className="form-control"
                  value={useYn}
                  onChange={e => setUseYn(e.target.value)}
                  id="useYn"
                >
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="page-btn-wrap both">
        <button
          type="button"
          className="btn xlg tertiary"
          onClick={handleCancelWriting}
        >
          돌아가기
        </button>
        {selectedTerm ? (
          <button
            type="button"
            className="btn xlg primary"
            onClick={handleUpdate}
          >
            수정하기
          </button>
        ) : (
          <button
            type="button"
            className="btn primary md"
            onClick={handleRegister}
          >
            등록
          </button>
        )}
      </div>
    </div>
  );

  return (
      <div className="inner in-between">
        {isWriting ? renderForm() : renderList()}
      </div>
  );
};

export default AdminTOS;
